import * as React from 'react';

import { ApplicationState, ConnectedReduxProps } from '../../../../store';
import { Checkbox, FormControl, FormControlLabel, MenuItem, Radio, RadioGroup, Select, SelectChangeEvent } from '@mui/material';
import { ReportTypeTC } from '../../../../business/models/typecode.enums';
import { RimLine } from '../../../../store/details/rim/types';
import { VatAndPaymentRequest as VatAndPayment } from '../../../../business/models/dto/rim/vat-and-payment.request.dto';
import { addRimLine, removeRimLine, rimLineAmountChange, rimLineDescriptionChange, rimLineInvoicePresentChange, rimLineItemChange, rimLineSectionChange, rimLineVatPercentageChange, rimLineVatThresholdChange, setCurrentTabAsDirty, updateVatSum } from '../../../../store/details/rim/actions';
import { getTypeCodesForLanguage } from '../../../../store/utils/typecodes/typecode-translations';

import { BelfiusCurrencyInput } from '../../../custom/belfius-currency-input';


import DaccaImportButton from '../../../custom/dacca-import-button';
import { Dispatch } from 'redux';
import { Ii18n } from '../../../../store/utils/contracts/i18n.interface';
import { connect } from 'react-redux';
import { getI18n } from '../../../../store/utils/i18n/strings.manager';
import styles from './rim-lines.module.css';
import { TypeCodeTranslation } from '../../../../business/models/shared.model';
import { RimLinesOverview } from './rim-lines-overview';
import { Role } from '../../../../store/shared/model';

interface PropsFromState {
  readonly rimSectionValues: TypeCodeTranslation[];
  readonly rimItemValues: TypeCodeTranslation[];
  readonly vatTrasholdTCValues: TypeCodeTranslation[];
  readonly vatPercentageValues: TypeCodeTranslation[];
  readonly rimLines: RimLine[];
  readonly currentRimLine: RimLine;
  readonly locked: boolean;
  readonly rimLinesAmountTotal: number;
  readonly rimLinesAmount80Total: number;
  readonly currentLanguage: string;
  readonly rimHeaderId: number;
  readonly conditions: string;
}

interface PropsFromDispatch {
  readonly addRimLine: typeof addRimLine;
  readonly removeRimLine: typeof removeRimLine;
  readonly updateVatSum: typeof updateVatSum;
  readonly rimLineSectionChange: typeof rimLineSectionChange;
  readonly rimLineItemChange: typeof rimLineItemChange;
  readonly rimLineVatThresholdChange: typeof rimLineVatThresholdChange;
  readonly rimLineVatPercentageChange: typeof rimLineVatPercentageChange;
  readonly rimLineAmountChange: typeof rimLineAmountChange;
  readonly rimLineDescriptionChange: typeof rimLineDescriptionChange;
  readonly rimLineInvoicePresentChange: typeof rimLineInvoicePresentChange;
  readonly setCurrentTabAsDirty: typeof setCurrentTabAsDirty;
}

type AllProps = PropsFromState & PropsFromDispatch & ConnectedReduxProps;

class RimLines extends React.Component<AllProps> {
  private localization: Ii18n;

  constructor(props: AllProps) {
    super(props);
    this.localization = getI18n();
  }

  render() {
    const daccaImportButton = (
      <DaccaImportButton
        {...this.props}
        reportType={ReportTypeTC.Rim}
        reference={this.props.rimHeaderId}
      />
    );

    return (
      <div className={styles.contentWrapper}>
        <div className={styles.rimLinesContent}>
          <div className={styles.row}>
            <div className={styles.column6x}>
              <div className={styles.row}>
                <div className={`${styles.item} ${styles.border}`}>
                  <FormControl className={styles.formControl}>
                    <Select
                      value={this.props.currentRimLine.rimSectionTC}
                      onChange={(event: SelectChangeEvent<string>) => {
                        this.props.rimLineSectionChange(event.target.value);
                      }}
                      displayEmpty
                      style={{ fontSize: '10pt' }}
                      className={styles.select}
                      disabled={this.props.locked}
                    >
                      <MenuItem value='' key='' disabled style={{ fontSize: '10pt' }}>
                        {this.localization.get('details.rim.rimLines.rimSections')}
                      </MenuItem>
                      {getTypeCodesForLanguage(this.props.rimSectionValues, this.props.currentLanguage).map(o =>
                        <MenuItem style={{ fontSize: '10pt' }}
                          value={o.typeCodeValue}
                          key={o.typeCodeValue}>{o.translation}
                        </MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
                <div className={`${styles.item} ${styles.border}`}>
                  <FormControl className={styles.formControl}>
                    <Select
                      value={this.props.currentRimLine.rimItemTC}
                      onChange={(event: SelectChangeEvent<string>) => {
                        this.props.rimLineItemChange(event.target.value);
                      }}
                      style={{ fontSize: '10pt' }}
                      displayEmpty
                      className={styles.select}
                      disabled={this.props.locked}
                    >
                      <MenuItem value='' key='' disabled style={{ fontSize: '10pt' }}>
                        {this.localization.get('details.rim.rimLines.rimItems')}
                      </MenuItem>
                      {getTypeCodesForLanguage(this.props.currentRimLine.rimItemValuesBySection, this.props.currentLanguage).map(o =>
                        <MenuItem
                          value={o.typeCodeValue}
                          key={o.typeCodeValue}
                          style={{ fontSize: '10pt' }}
                          disabled={!o.enabled}>
                          {o.translation}
                        </MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
                <div className={`${styles.item} ${styles.border}`}>
                  <BelfiusCurrencyInput className={styles.input}
                    placeholder={this.localization.get('details.rim.valueAndCause.value')}
                    disabled={this.props.locked || !this.props.currentRimLine.amountEnabled}
                    allowNegative={true}
                    value={this.props.currentRimLine.amount}
                    onValueChange={(values: { value: string }) => {
                      this.props.rimLineAmountChange(values.value);
                    }}
                  />
                </div>
                <div className={`${styles.item} ${styles.border}`}>
                  <FormControl className={styles.formControl}>
                    <Select
                      value={this.props.currentRimLine.vatThresholdTC}
                      onChange={(event: SelectChangeEvent<string>) => {
                        this.props.rimLineVatThresholdChange(event.target.value);
                      }}
                      displayEmpty
                      style={{ fontSize: '10pt' }}
                      className={styles.select}
                      disabled={this.props.locked || !this.props.currentRimLine.vatEnabled}
                    >
                      <MenuItem value='' key='' disabled style={{ fontSize: '10pt' }}>
                        {this.localization.get('details.rim.rimLines.vatTrashold')}
                      </MenuItem>
                      {getTypeCodesForLanguage(this.props.vatTrasholdTCValues, this.props.currentLanguage).map(o =>
                        <MenuItem
                          value={o.typeCodeValue}
                          style={{ fontSize: '10pt' }}
                          key={o.typeCodeValue}>{o.translation}
                        </MenuItem>)}
                    </Select>
                  </FormControl>
                </div>
              </div>
              <div className={styles.row}>
                <div className={`${styles.item} ${styles.border}`}>
                  <input className={styles.input}
                    disabled={this.props.locked}
                    placeholder={this.localization.get('details.rim.rimLines.description')}
                    value={this.props.currentRimLine.description}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      this.props.rimLineDescriptionChange(event.target.value);
                    }} />
                </div>
              </div>
            </div >
            <div className={styles.column}>
              <div className={`${styles.item} ${styles.border}`}>
                <RadioGroup
                  value={this.props.currentRimLine.vatPercentageTC.toString()}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                    this.props.rimLineVatPercentageChange(event.target.value);
                  }}
                  className={styles.radio}
                  style={{ flexDirection: 'row', fontSize: '10pt' }}
                >
                  {getTypeCodesForLanguage(this.props.vatPercentageValues, this.props.currentLanguage).map(x => <FormControlLabel
                    disabled={this.props.locked || !this.props.currentRimLine.vatEnabled || !this.props.currentRimLine.vatPercentageEnabled}
                    value={x.typeCodeValue}
                    key={x.typeCodeValue}
                    style={{ fontSize: '10pt !important' }}
                    control={<Radio />}
                    label={x.translation} />)}
                </RadioGroup>
              </div>
              <div className={`${styles.item} ${styles.border}`}>
                <div className={styles.checkbox}>
                  <Checkbox
                    disabled={this.props.locked || !this.props.currentRimLine.invoicePresentEnabled}
                    checked={this.props.currentRimLine.invoicePresent}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      this.props.rimLineInvoicePresentChange(event.target.checked);
                    }}
                    value='true' />
                  {this.localization.get('details.rim.rimLines.offer')}
                </div>
              </div>
            </div>
            <div className={styles.border}>
              <div className={styles.addbutton}
                onClick={() => {
                  if (!this.props.locked) {
                    this.props.addRimLine();
                  }
                }}>
                <img alt="" src={require('../../../../assets/images/add_85x85.png')} />
              </div>
            </div>
          </div >
          <div className={styles.border}>
            <RimLinesOverview
              rimLines={this.props.rimLines}
              rimItemValues={this.props.rimItemValues}
              rimSectionValues={this.props.rimSectionValues}
              removeRimLine={this.props.removeRimLine}
              conditions={this.props.conditions}
              currentLanguage={this.props.currentLanguage}
              locked={this.props.locked}
              vatPercentageValues={this.props.vatPercentageValues}
              vatTrasholdTCValues={this.props.vatTrasholdTCValues}
              localization={this.localization}
            />
          </div>
        </div >
        <div className={styles.daccaButtonContent}>
          {daccaImportButton}
        </div >
      </div >
    );
  }
}

const mapStateToProps = ({ details: { detailsRim, detailsPolicy }, shared, caseState: { selectedCase }}: ApplicationState): PropsFromState => ({
  rimSectionValues: detailsRim.rimTypeCodes.rimSectionValues,
  rimItemValues: detailsRim.rimTypeCodes.rimItemValues,
  vatTrasholdTCValues: detailsRim.rimTypeCodes.vatThresholdValues,
  vatPercentageValues: detailsRim.rimTypeCodes.vatPercentageValues,
  rimLines: detailsRim.rimLines,
  locked: detailsRim.locked === '' ? false : detailsRim.locked || selectedCase.blocked || !shared.roles.includes(Role.ExFiLoAd),
  currentRimLine: detailsRim.currentRimLine,
  rimLinesAmountTotal: detailsRim.rimLinesAmountTotal,
  rimLinesAmount80Total: detailsRim.rimLinesAmount80Total,
  currentLanguage: shared.currentLanguage,
  rimHeaderId: detailsRim.rimHeaderId,
  conditions: detailsPolicy.policy.conditions
});

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
  addRimLine: () => dispatch(addRimLine()),
  removeRimLine: (sequence: number) => dispatch(removeRimLine(sequence)),
  updateVatSum: (vatAndPayment: VatAndPayment) => dispatch(updateVatSum(vatAndPayment)),
  rimLineSectionChange: (rimSelectionTC: string) => dispatch(rimLineSectionChange(rimSelectionTC)),
  rimLineItemChange: (rimItemTC: string) => dispatch(rimLineItemChange(rimItemTC)),
  rimLineVatThresholdChange: (vatThresholdTC: string) => dispatch(rimLineVatThresholdChange(vatThresholdTC)),
  rimLineVatPercentageChange: (vatPercentageTC: string) => dispatch(rimLineVatPercentageChange(vatPercentageTC)),
  rimLineAmountChange: (amount: string) => dispatch(rimLineAmountChange(amount)),
  rimLineDescriptionChange: (description: string) => dispatch(rimLineDescriptionChange(description)),
  rimLineInvoicePresentChange: (invoicePresent: boolean) => dispatch(rimLineInvoicePresentChange(invoicePresent)),
  setCurrentTabAsDirty: () => dispatch(setCurrentTabAsDirty())
});

export default connect(mapStateToProps, mapDispatchToProps)(RimLines);
